import { DateTime, Duration, Interval } from "luxon";

export const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
export const EXPLICIT_TIMEZONE_PATTERN = /(?:Z|[+-]\d{2}:\d{2})$/;

export function toLocaleDate(date: string, format: Intl.DateTimeFormatOptions = DateTime.DATE_SHORT) {
  const parsedDate = DateTime.fromISO(date);
  if (!parsedDate.isValid) {
    return "";
  }
  return parsedDate.toLocaleString(format);
}

export function toUTCFormat(date: string, format: string = "LLL dd, yyyy") {
  const parsedDate = DateTime.fromISO(date);
  if (!parsedDate.isValid) {
    return null;
  }
  return parsedDate.toUTC().toFormat(format);
}

export function toFormat(date: string, format: string = "LLL dd, yyyy") {
  const parsedDate = DateTime.fromISO(date);
  if (!parsedDate.isValid) {
    return null;
  }
  return parsedDate.toFormat(format);
}

const EMBEDDED_ISO_DATE_PATTERN = /\b\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?)?\b/g;

/** Replaces ISO-8601 date/datetime tokens in free text with `toFormat` display strings. */
export function formatIsoDatesInText(text: string, format: string = "LLL dd, yyyy"): string {
  return text.replace(EMBEDDED_ISO_DATE_PATTERN, (iso) => toFormat(iso, format) ?? iso);
}

export const calculateYearsOfMembership = (days?: number) => {
  if (!days) return null;

  const now = DateTime.now();
  const startDate = now.minus({ days });
  const diff = now.diff(startDate, ["years", "months"], { conversionAccuracy: "longterm" });

  const years = Math.floor(diff.years ?? 0);
  const months = Math.floor(diff.months ?? 0);

  if (years === 0) {
    return `${months} mo`;
  }
  return `${years} yrs ${months} mos`;
};

/**
 * Calculates the time remaining until a deadline date.
 * Returns a human-readable string like "2 years, 3 months, 5 days" or "1 month, 2 days".
 * Returns null if the deadline is invalid, in the past, or no date is provided.
 *
 * Excludes zero values and rounds all numbers to integers (no decimals).
 *
 * @param deadlineDate - ISO date string, null, or undefined
 * @returns Formatted time remaining string or null
 */
export function calculateTimeRemaining(deadlineDate: string | null | undefined): string | null {
  if (!deadlineDate) return null;

  const deadline = DateTime.fromISO(deadlineDate);
  const now = DateTime.now();

  if (!deadline.isValid || deadline.startOf("day") < now.startOf("day")) {
    return null;
  }

  const diff = deadline.diff(now, ["years", "months", "days"], { conversionAccuracy: "longterm" });

  const years = Math.floor(diff.years ?? 0);
  const months = Math.floor(diff.months ?? 0);
  const days = Math.floor(diff.days ?? 0);

  const parts: string[] = [];

  if (years > 0) {
    parts.push(`${years} ${years === 1 ? "year" : "years"}`);
  }
  if (months > 0) {
    parts.push(`${months} ${months === 1 ? "month" : "months"}`);
  }
  if (days > 0) {
    parts.push(`${days} ${days === 1 ? "day" : "days"}`);
  }

  if (parts.length === 0) {
    return null;
  }

  return parts.join(", ");
}

/**
 * Formats employment-style duration from start through end.
 * When {@link calculateMonthsBetween} is under 1 month, uses calendar days (e.g. "15 days").
 * Otherwise whole years plus whole remaining months (e.g. "2 years, 3 months"), no day units.
 *
 * @param startDate - ISO date string (date or datetime)
 * @param endDate - ISO date string or `Date` (e.g. `new Date()` for through today)
 * @returns `null` if either date is invalid or the end is before the start
 */
export function formatDurationBetweenDates(
  startDate: string | null | undefined,
  endDate: string | Date | null | undefined
): string | null {
  if (!startDate || endDate === null || endDate === undefined) {
    return null;
  }

  const start = DateTime.fromISO(startDate).startOf("day");
  if (!start.isValid) {
    return null;
  }

  const end =
    typeof endDate === "string"
      ? DateTime.fromISO(endDate).startOf("day")
      : DateTime.fromJSDate(endDate).startOf("day");
  if (!end.isValid) {
    return null;
  }

  if (end < start) {
    return null;
  }

  const totalMonths = calculateMonthsBetween(startDate, endDate);

  if (totalMonths < 1) {
    const dayCount = Math.floor(end.diff(start, "days").days);
    if (dayCount <= 0) {
      return "0 days";
    }
    return `${dayCount} ${dayCount === 1 ? "day" : "days"}`;
  }

  const fullYears = Math.floor(totalMonths / 12);
  const monthRemainder = Math.floor(totalMonths - fullYears * 12);

  const parts: string[] = [];

  if (fullYears > 0) {
    parts.push(`${fullYears} ${fullYears === 1 ? "year" : "years"}`);
  }

  if (monthRemainder > 0 || fullYears === 0) {
    const label = monthRemainder === 1 ? "month" : "months";
    parts.push(`${monthRemainder} ${label}`);
  }

  return parts.join(", ");
}

export function toDateTimeFormat(date: string, format: string = "LLL dd, yyyy t") {
  const parsedDate = DateTime.fromISO(date);
  if (!parsedDate.isValid) {
    return null;
  }
  return parsedDate.toFormat(format);
}

export type DateFormatOptions = {
  format?: string;
  tz?: string;
};

export function toDateFormat(date: string, options: DateFormatOptions = {}) {
  const { format, tz } = options;
  const datetimeOptions = tz ? { zone: tz } : {};
  const parsedDate = DateTime.fromISO(date, datetimeOptions);
  if (!parsedDate.isValid) {
    return null;
  }
  return parsedDate.toFormat(format || "LLL dd, yyyy");
}

export function toDateOnlyInTimezone(date: string | Date | null | undefined, timezone: string): string | undefined {
  if (!date) return undefined;

  if (typeof date === "string") {
    if (DATE_ONLY_PATTERN.test(date)) {
      return date;
    }

    const parsedDate = EXPLICIT_TIMEZONE_PATTERN.test(date)
      ? DateTime.fromISO(date, { setZone: true }).setZone(timezone)
      : DateTime.fromISO(date, { zone: timezone });

    return parsedDate.isValid ? parsedDate.toFormat("yyyy-MM-dd") : undefined;
  }

  const parsedDate = DateTime.fromJSDate(date, { zone: timezone });
  return parsedDate.isValid ? parsedDate.toFormat("yyyy-MM-dd") : undefined;
}

export function getTime(
  date: string,
  format: Intl.DateTimeFormatOptions = DateTime.TIME_SIMPLE,
  timezone: string = "utc"
) {
  const parsedDate = DateTime.fromISO(date, { zone: timezone });
  if (!parsedDate.isValid) {
    return null;
  }
  return parsedDate.toLocaleString(format);
}

export function getHours(duration: string) {
  const parsedTime = Duration.fromISO(duration);
  if (!parsedTime.isValid) {
    return null;
  }

  let humanDuration = parsedTime.toHuman({ unitDisplay: "short" });
  return humanDuration.replace(/,/g, "");
}

export function toISO(date: string) {
  const parsedDate = DateTime.fromISO(date);
  if (!parsedDate.isValid) {
    return "";
  }
  return parsedDate.toISO();
}

export function getAge(date: string): Duration | null {
  const parsedDate = DateTime.fromISO(date);
  if (!parsedDate.isValid) {
    return null;
  }
  return Interval.fromDateTimes(parsedDate, DateTime.now()).toDuration(["years", "months", "days", "hours"]);
}

export const getDiffYears = (date: string) => DateTime.now().diff(DateTime.fromISO(date), ["years"]).rescale().years;

export function isInFuture(date: string) {
  const parsedDate = DateTime.fromISO(date);
  if (!parsedDate.isValid) {
    return false;
  }
  return parsedDate > DateTime.now();
}

/**
 * Checks if a date is before today (comparing at day level, ignoring time).
 * Returns true if the date is before today, false if it's today or in the future.
 *
 * @param date - ISO date string
 * @returns true if date is before today, false otherwise
 */
export function isBeforeToday(date: string | null | undefined): boolean {
  if (!date) return false;

  const parsedDate = DateTime.fromISO(date).startOf("day");
  if (!parsedDate.isValid) {
    return false;
  }

  const today = DateTime.now().startOf("day");
  return parsedDate < today;
}

export function getOverdueDays(date: string | null | undefined): number {
  if (!isBeforeToday(date)) return 0;
  return Math.floor(DateTime.now().startOf("day").diff(DateTime.fromISO(date!).startOf("day"), "days").days);
}

export function isValidDate(value: any, format: string = "LLL dd, yyyy"): boolean {
  if (typeof value !== "string") {
    return false;
  }
  const date = DateTime.fromFormat(value, format);
  return date.isValid;
}

export function isSameDay(startDate: string, endDate: string, timezone?: string): boolean {
  const startDateTime = timezone ? DateTime.fromISO(startDate, { zone: timezone }) : DateTime.fromISO(startDate);
  const endDateTime = timezone ? DateTime.fromISO(endDate, { zone: timezone }) : DateTime.fromISO(endDate);

  if (!startDateTime.isValid || !endDateTime.isValid) {
    return false;
  }

  return startDateTime.hasSame(endDateTime, "day");
}

/**
 * Replicates Oracle's MONTHS_BETWEEN: uses a fixed 31-day month for the
 * fractional portion, and returns an exact integer when both dates fall on
 * the same day-of-month or both are the last day of their respective months.
 *
 * Extracts year/month/day from strings directly instead of constructing a
 * Date object, because `new Date("2024-01-31")` is parsed as UTC midnight
 * and `.getDate()` returns the local-timezone day (e.g. 30 in US Central).
 */
export function calculateMonthsBetween(startDate: string | Date, endDate: string | Date): number {
  const start = extractDateParts(startDate);
  const end = extractDateParts(endDate);
  if (!start || !end) return 0;

  const wholeMonths = (end.year - start.year) * 12 + (end.month - start.month);

  const startIsLastDay = start.day === daysInMonth(start.year, start.month);
  const endIsLastDay = end.day === daysInMonth(end.year, end.month);

  if (start.day === end.day || (startIsLastDay && endIsLastDay)) {
    return wholeMonths;
  }

  return Math.round((wholeMonths + (end.day - start.day) / 31) * 10) / 10;
}

function extractDateParts(date: string | Date): { year: number; month: number; day: number } | null {
  if (typeof date === "string") {
    const [year, month, day] = date.split("T")[0].split("-").map(Number);
    if (!year || !month || !day) return null;
    return { year, month, day };
  }
  if (isNaN(date.getTime())) return null;
  return { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() };
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}
